// Template slot bindings (presentation contract section 4.1): a source plus `|` formatters.
// There are no expressions; anything that fails to resolve yields null and the slot stays empty.

export type BindingSource =
  | { kind: 'object'; field: 'label' | 'owner' | 'count' }
  | { kind: 'field'; key: string }
  | { kind: 'fields' }
  | { kind: 'catalog'; field: 'name' | 'text' }
  | { kind: 'catalogKey' }
  | { kind: 'catalogField'; key: string }
  | { kind: 'asset'; id: string }
  | { kind: 'icon'; key: string }
  | { kind: 'seatColor' }
  | { kind: 'literal'; text: string };

export type Formatter =
  | { kind: 'number' }
  | { kind: 'costRow'; prefix: string }
  | { kind: 'icon' }
  | { kind: 'map'; name: string }
  | { kind: 'upper' }
  /** 'twig:2 card:1' (key:count pairs) as an icon row, for gains a theme map spells out. */
  | { kind: 'gains' }
  /** Literal words before the value, joined by one space: `prefix:Free with` turns Inn into "Free with Inn". */
  | { kind: 'prefix'; text: string }
  /** Named numeric fields as an icon row (non-zero values in the listed order): `catalog.fields|stats:attack,light`. */
  | { kind: 'stats'; keys: string[] };

export interface Binding { source: BindingSource; formatters: Formatter[] }

const KEY = /^[A-Za-z0-9][A-Za-z0-9:_.\-]{0,127}$/;
const ASSET = /^[A-Za-z0-9{][A-Za-z0-9:_.\-/{}]{0,127}$/;

export function parseBinding(text: string): Binding | string {
  const parts = text.split('|');
  const head = parts[0]!.trim();
  let source: BindingSource;
  if (head.startsWith("'")) {
    if (!head.endsWith("'") || head.length < 2) return `Unterminated literal in "${text}".`;
    const literal = head.slice(1, -1);
    if (literal.length > 64) return 'Literal text is limited to 64 characters.';
    source = { kind: 'literal', text: literal };
  } else if (head === 'object.label' || head === 'object.owner' || head === 'object.count') {
    source = { kind: 'object', field: head.slice(7) as 'label' | 'owner' | 'count' };
  } else if (head === 'fields') {
    source = { kind: 'fields' };
  } else if (head.startsWith('fields.')) {
    const key = head.slice(7); if (!KEY.test(key)) return `Invalid field key in "${text}".`;
    source = { kind: 'field', key };
  } else if (head === 'catalog.key') {
    source = { kind: 'catalogKey' };
  } else if (head === 'catalog.name' || head === 'catalog.text') {
    source = { kind: 'catalog', field: head.slice(8) as 'name' | 'text' };
  } else if (head.startsWith('catalog.fields.')) {
    const key = head.slice(15); if (!KEY.test(key)) return `Invalid catalog field key in "${text}".`;
    source = { kind: 'catalogField', key };
  } else if (head.startsWith('asset:')) {
    const id = head.slice(6); if (!ASSET.test(id)) return `Invalid asset reference in "${text}".`;
    source = { kind: 'asset', id };
  } else if (head.startsWith('icon:')) {
    const key = head.slice(5); if (!KEY.test(key)) return `Invalid icon key in "${text}".`;
    source = { kind: 'icon', key };
  } else if (head === 'seat.color') {
    source = { kind: 'seatColor' };
  } else {
    return `Unknown binding source "${head}".`;
  }
  const formatters: Formatter[] = [];
  for (const raw of parts.slice(1)) {
    const f = raw.trim();
    if (f === 'number' || f === 'icon' || f === 'upper' || f === 'gains') formatters.push({ kind: f });
    else if (f.startsWith('costRow:')) formatters.push({ kind: 'costRow', prefix: f.slice(8) });
    else if (f.startsWith('map:') && KEY.test(f.slice(4))) formatters.push({ kind: 'map', name: f.slice(4) });
    else if (f.startsWith('stats:') && f.slice(6).split(',').every(k => KEY.test(k)) && f.slice(6).split(',').length <= 12) formatters.push({ kind: 'stats', keys: f.slice(6).split(',') });
    else if (f.startsWith('prefix:') && /^[A-Za-z0-9][A-Za-z0-9 ,.'-]{0,31}$/.test(f.slice(7))) formatters.push({ kind: 'prefix', text: f.slice(7) });
    else return `Unknown formatter "${f}".`;
  }
  return { source, formatters };
}

export type Scalar = string | number | boolean;

export interface BindingContext {
  object: { label: string; owner?: string | null; count?: number | null; kind?: string; catalog?: string | null; fields?: Record<string, Scalar> };
  catalog: { name: string; text?: string; fields?: Record<string, Scalar> } | null;
  resources: { key: string; label: string }[];
  assetUrl: (id: string) => string | null;
  icon: (key: string) => Resolved | null;
  map: (name: string, value: string) => string | null;
  seatColor: (owner: string) => string | null;
}

export type Resolved =
  | { kind: 'text'; text: string }
  | { kind: 'image'; url: string; frame?: { x: number; y: number; w: number; h: number }; sheet?: { w: number; h: number } }
  | { kind: 'glyph'; name: string }
  | { kind: 'color'; value: string }
  | { kind: 'costs'; items: { key: string; label: string; value: number; icon: Resolved | null }[] };

export function resolveBinding(binding: Binding, ctx: BindingContext): Resolved | null {
  let value: unknown = sourceValue(binding.source, ctx);
  if (value === null || value === undefined) return null;
  let resolved: Resolved | null = null;
  for (const f of binding.formatters) {
    switch (f.kind) {
      case 'number': {
        const n = Number(value);
        if (!Number.isFinite(n)) return null;
        value = Number.isInteger(n) ? String(n) : n.toFixed(1);
        break;
      }
      case 'upper': value = String(value).toUpperCase(); break;
      case 'prefix': value = `${f.text} ${String(value)}`; break;
      case 'map': {
        const mapped = ctx.map(f.name, String(value));
        if (mapped === null) return null;
        value = mapped;
        break;
      }
      case 'icon': resolved = ctx.icon(String(value)); if (!resolved) return null; break;
      case 'gains': {
        const items = String(value).trim().split(/\s+/).map(part => {
          const colon = part.lastIndexOf(':');
          const key = colon > 0 ? part.slice(0, colon) : part, n = colon > 0 ? Number(part.slice(colon + 1)) : 1;
          return { key, label: ctx.resources.find(r => r.key === key)?.label ?? key, value: n, icon: ctx.icon(key) };
        }).filter(i => i.key && Number.isFinite(i.value) && i.value > 0);
        if (items.length === 0) return null;
        resolved = { kind: 'costs', items };
        break;
      }
      case 'stats': {
        if (typeof value !== 'object') return null;
        const fields = value as Record<string, Scalar>;
        const items = f.keys.map(key => ({ key, label: ctx.resources.find(r => r.key === key)?.label ?? key, value: Number(fields[key] ?? 0), icon: ctx.icon(key) }))
          .filter(i => Number.isFinite(i.value) && i.value !== 0);
        resolved = { kind: 'costs', items };
        break;
      }
      case 'costRow': {
        if (typeof value !== 'object') return null;
        const fields = value as Record<string, Scalar>;
        const items = ctx.resources
          .map(r => ({ key: r.key, label: r.label, value: Number(fields[f.prefix + r.key] ?? 0), icon: ctx.icon(r.key) }))
          .filter(i => Number.isFinite(i.value) && i.value > 0);
        resolved = { kind: 'costs', items };
        break;
      }
    }
  }
  if (resolved) return resolved;
  if (binding.source.kind === 'asset') { const url = ctx.assetUrl(String(value)); return url ? { kind: 'image', url } : null; }
  if (binding.source.kind === 'icon') return ctx.icon(String(value));
  if (typeof value === 'object') return null;
  const text = String(value);
  if (/^#[0-9a-fA-F]{6}$/.test(text)) return { kind: 'color', value: text };
  if (binding.source.kind === 'seatColor') return { kind: 'color', value: text };
  return text === '' ? null : { kind: 'text', text };
}

function sourceValue(source: BindingSource, ctx: BindingContext): unknown {
  switch (source.kind) {
    case 'literal': return source.text;
    case 'object': return ctx.object[source.field] ?? null;
    case 'field': return ctx.object.fields?.[source.key] ?? ctx.catalog?.fields?.[source.key] ?? null;
    case 'fields': return { ...(ctx.catalog?.fields ?? {}), ...(ctx.object.fields ?? {}) };
    case 'catalog': return ctx.catalog?.[source.field] ?? null;
    case 'catalogKey': return ctx.object.catalog ?? null;
    case 'catalogField': return ctx.catalog?.fields?.[source.key] ?? null;
    case 'asset': return expandAsset(source.id, ctx.object);
    case 'icon': return source.key;
    case 'seatColor': return ctx.object.owner ? ctx.seatColor(ctx.object.owner) : null;
  }
}

export function expandAsset(id: string, object: { catalog?: string | null; kind?: string }): string | null {
  if (id.includes('{catalog}') && !object.catalog) return null;
  if (id.includes('{kind}') && !object.kind) return null;
  return id.replaceAll('{catalog}', object.catalog ?? '').replaceAll('{kind}', object.kind ?? '');
}
